// Home screen shared by Athavita Docs, Sheets and Slides (Google Docs /
// Office.com style): start a new file from blank or a template, then find
// a file — Recent / Starred / Shared with me / Owned by me, search, sort,
// grid or list. Identical in all three apps; everything app-specific comes
// in through `config` (see homeConfig.ts).

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
    AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Cloud, FolderOpen, LayoutGrid, List as ListIcon, Loader2, Monitor, Plus,
    RefreshCw, Search, Star, Users, X, Clock, User
} from "lucide-react";
import { homeApi } from "./homeApi";
import {
    SUITE_URLS, driveUrlFor, errorText, externalUrlFor, readPref, readPrefText, sortItems, withExtension, writePref,
    type EditorLaunchState, type HomeConfig, type HomeItem, type SortKey, type SuiteCatalogue, type SuiteTemplate, type TemplateApp
} from "./homeModel";
import {
    BuiltinTemplateArt, FileCollection, MoreTemplatesTile, SkeletonCollection, SuiteTemplateArt, TemplateTile,
    TemplateTileSkeleton, type ItemHandlers
} from "./HomeFiles";
import {
    ActionMenu, BTN_ICON, BTN_OUTLINE, BTN_PRIMARY, ConfirmDialog, RenameDialog, SELECT, Toast, cx, type ToastState
} from "./HomeUi";

type Tab = "recent" | "starred" | "shared" | "owned";
type View = "grid" | "list";
type Scope = "app" | "all";
type Status = "idle" | "loading" | "ready" | "error";

interface TabData {
    status: Status;
    items: HomeItem[];
    error: string | null;
}

const TABS: readonly Tab[] = ["recent", "starred", "shared", "owned"];
const TAB_LABEL: Record<Tab, string> = {
    recent: "Recent",
    starred: "Starred",
    shared: "Shared with me",
    owned: "Owned by me"
};
const TAB_ICON: Record<Tab, typeof Clock> = { recent: Clock, starred: Star, shared: Users, owned: User };
const SORTS: readonly SortKey[] = ["opened", "name", "modified"];
const SORT_LABEL: Record<SortKey, string> = { opened: "Last opened", name: "Name", modified: "Last modified" };
const VIEWS: readonly View[] = ["grid", "list"];
const SCOPES: readonly Scope[] = ["app", "all"];
const SEARCH_DEBOUNCE_MS = 250;
const SERVER_SEARCH_MIN = 2;

const EMPTY: TabData = { status: "idle", items: [], error: null };

function mapTabs(tabs: Record<Tab, TabData>, fn: (data: TabData, tab: Tab) => TabData): Record<Tab, TabData> {
    return {
        recent: fn(tabs.recent, "recent"),
        starred: fn(tabs.starred, "starred"),
        shared: fn(tabs.shared, "shared"),
        owned: fn(tabs.owned, "owned")
    };
}

async function fetchTab(tab: Tab, scope: Scope, config: HomeConfig, token: string | undefined): Promise<HomeItem[]> {
    switch (tab) {
        case "recent":
            return homeApi.recent(100, token);
        case "starred":
            return homeApi.favorites(token);
        case "shared":
            return homeApi.sharedWithMe(token);
        case "owned": {
            // Drive has no "every file of type X" listing: name searches for
            // the app's extensions find them across all folders, and recent /
            // starred / My Drive's top level fill in anything search missed.
            const terms = scope === "all" ? ["."] : config.searchTerms;
            const results = await Promise.allSettled([
                ...terms.map((term) => homeApi.search(term, 200, token)),
                homeApi.recent(100, token),
                homeApi.favorites(token),
                homeApi.listFolder(null, token)
            ]);
            const byId = new Map<string, HomeItem>();
            let firstError: unknown = null;
            let anyOk = false;
            for (const result of results) {
                if (result.status === "rejected") {
                    firstError ??= result.reason;
                    continue;
                }
                anyOk = true;
                for (const item of result.value) {
                    if (!item.ownerEmail && !byId.has(item.id)) byId.set(item.id, item);
                }
            }
            if (!anyOk) throw firstError;
            return [...byId.values()];
        }
    }
}

// ── Shared template library ─────────────────────────────────────────────
// Fetched once per page load (Drive's "New" gallery lists the same
// entries); a failed fetch isn't cached, so Retry / the next visit tries again.

/** An industry this app has templates for, in catalogue order. */
interface IndustryOption {
    name: string;
    count: number;
}

type Catalogue =
    | { status: "loading" }
    | { status: "ready"; blank: SuiteTemplate | null; templates: SuiteTemplate[]; industries: IndustryOption[] }
    | { status: "error"; error: string };

let catalogueRequest: Promise<SuiteCatalogue> | null = null;

function loadCatalogue(token: string | undefined): Promise<SuiteCatalogue> {
    catalogueRequest ??= homeApi.templates(token).catch((err: unknown) => {
        catalogueRequest = null;
        throw err;
    });
    return catalogueRequest;
}

function useCatalogue(app: TemplateApp, token: string | undefined): [Catalogue, () => void] {
    const [state, setState] = useState<Catalogue>({ status: "loading" });
    const [nonce, setNonce] = useState(0);
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const hasToken = Boolean(token);

    useEffect(() => {
        if (!hasToken) return;
        let cancelled = false;
        setState({ status: "loading" });
        loadCatalogue(tokenRef.current)
            .then((all) => {
                if (cancelled) return;
                const mine = all.templates.filter((t) => t.app === app);
                const templates = mine.filter((t) => !t.blank);
                const industries = all.industries
                    .map((name) => ({ name, count: templates.filter((t) => t.industry === name).length }))
                    .filter((i) => i.count > 0);
                setState({ status: "ready", blank: mine.find((t) => t.blank) ?? null, templates, industries });
            })
            .catch((err: unknown) => {
                if (!cancelled) setState({ status: "error", error: errorText(err) });
            });
        return () => { cancelled = true; };
    }, [app, hasToken, nonce]);

    return [state, useCallback(() => setNonce((n) => n + 1), [])];
}

export function SuiteHome({ config }: { config: HomeConfig }) {
    const navigate = useNavigate();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const tokenRef = useRef(token);
    tokenRef.current = token;

    const prefKey = (name: string) => `athavita.home.${config.appId}.${name}`;
    const [tab, setTab] = useState<Tab>("recent");
    const [view, setView] = useState<View>(() => readPref(prefKey("view"), VIEWS, "grid"));
    const [sort, setSort] = useState<SortKey>(() => readPref(prefKey("sort"), SORTS, "opened"));
    const [scope, setScope] = useState<Scope>(() =>
        config.allFilesOption ? readPref(prefKey("scope"), SCOPES, "app") : "app"
    );

    // ── Tab data ─────────────────────────────────────────────────────────
    const [tabs, setTabs] = useState<Record<Tab, TabData>>(() => mapTabs({ recent: EMPTY, starred: EMPTY, shared: EMPTY, owned: EMPTY }, (d) => d));
    const seqRef = useRef<Record<Tab, number>>({ recent: 0, starred: 0, shared: 0, owned: 0 });

    const loadTab = useCallback(async (which: Tab) => {
        const seq = ++seqRef.current[which];
        setTabs((t) => ({ ...t, [which]: { ...t[which], status: "loading", error: null } }));
        try {
            const items = await fetchTab(which, scope, config, tokenRef.current);
            if (seq !== seqRef.current[which]) return;
            setTabs((t) => ({ ...t, [which]: { status: "ready", items, error: null } }));
        } catch (err) {
            if (seq !== seqRef.current[which]) return;
            setTabs((t) => ({ ...t, [which]: { ...t[which], status: "error", error: errorText(err) } }));
        }
    }, [scope, config]);

    const activeTab = tabs[tab];
    useEffect(() => {
        if (activeTab.status === "idle") void loadTab(tab);
    }, [tab, activeTab.status, loadTab]);

    // ── Search ───────────────────────────────────────────────────────────
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [query]);

    // Drive search only covers the user's own files, so "Shared with me"
    // filters its own list instead.
    const serverSearch = debounced.length >= SERVER_SEARCH_MIN && tab !== "shared";
    const [search, setSearch] = useState<{ q: string; data: TabData }>({ q: "", data: EMPTY });
    const [searchNonce, setSearchNonce] = useState(0);
    useEffect(() => {
        if (!serverSearch) return;
        let cancelled = false;
        setSearch((s) => ({ q: debounced, data: { ...s.data, status: "loading", error: null } }));
        homeApi.search(debounced, 100, tokenRef.current)
            .then((items) => {
                if (!cancelled) setSearch({ q: debounced, data: { status: "ready", items, error: null } });
            })
            .catch((err: unknown) => {
                if (!cancelled) setSearch((s) => ({ q: debounced, data: { ...s.data, status: "error", error: errorText(err) } }));
            });
        return () => { cancelled = true; };
    }, [serverSearch, debounced, searchNonce]);

    // ── What's on screen ─────────────────────────────────────────────────
    const source: TabData = serverSearch ? search.data : activeTab;
    const visible = useMemo(() => {
        let items = source.items.filter(
            (i) => i.kind === "File" && !i.isTrashed && (scope === "all" || config.openable.test(i.name))
        );
        if (serverSearch) {
            if (tab === "starred") items = items.filter((i) => i.isFavorite);
        } else {
            const needle = query.trim().toLowerCase();
            if (needle) items = items.filter((i) => i.name.toLowerCase().includes(needle));
        }
        return sortItems(items, sort);
    }, [source.items, scope, config.openable, serverSearch, tab, query, sort]);

    const loading = source.status === "loading" || source.status === "idle";
    const showSkeleton = loading && visible.length === 0;
    const plural = scope === "all" ? "files" : config.nounPlural;

    const refresh = () => {
        setTabs((t) => mapTabs(t, (d, which) => (which !== tab && d.status === "ready" ? { ...d, status: "idle" } : d)));
        void loadTab(tab);
        if (serverSearch) setSearchNonce((n) => n + 1);
    };
    const retry = () => (serverSearch ? setSearchNonce((n) => n + 1) : void loadTab(tab));

    const changeScope = (next: Scope) => {
        setScope(next);
        writePref(prefKey("scope"), next);
        seqRef.current.owned += 1;
        setTabs((t) => ({ ...t, owned: EMPTY }));
    };
    const changeView = (next: View) => { setView(next); writePref(prefKey("view"), next); };
    const changeSort = (next: SortKey) => { setSort(next); writePref(prefKey("sort"), next); };

    // ── Actions ──────────────────────────────────────────────────────────
    const [toast, setToast] = useState<ToastState | null>(null);
    const toastSeq = useRef(0);
    const notify = useCallback((message: string, tone: ToastState["tone"] = "info", action?: ToastState["action"]) => {
        setToast({ id: ++toastSeq.current, message, tone, action });
    }, []);
    const dismissToast = useCallback(() => setToast(null), []);

    const patchEverywhere = useCallback((id: string, patch: Partial<HomeItem> | null) => {
        const apply = (items: HomeItem[]) =>
            patch === null ? items.filter((i) => i.id !== id) : items.map((i) => (i.id === id ? { ...i, ...patch } : i));
        setTabs((t) => mapTabs(t, (d) => ({ ...d, items: apply(d.items) })));
        setSearch((s) => ({ ...s, data: { ...s.data, items: apply(s.data.items) } }));
    }, []);

    const [renaming, setRenaming] = useState<HomeItem | null>(null);
    const [trashing, setTrashing] = useState<HomeItem | null>(null);

    const editorHref = useCallback(
        (item: { id: string }) => `${config.editorPath}?fileId=${encodeURIComponent(item.id)}`,
        [config.editorPath]
    );
    const openItem = useCallback((item: HomeItem) => {
        if (config.openable.test(item.name)) navigate(editorHref(item));
        else window.open(externalUrlFor(item), "_blank", "noopener,noreferrer");
    }, [config.openable, editorHref, navigate]);

    const handlers = useMemo<ItemHandlers>(() => ({
        open: openItem,
        openNewTab: (item) => {
            const url = config.openable.test(item.name)
                ? new URL(editorHref(item), window.location.origin).toString()
                : externalUrlFor(item);
            window.open(url, "_blank", "noopener,noreferrer");
        },
        rename: (item) => setRenaming(item),
        copy: (item) => {
            void (async () => {
                try {
                    const copy = await homeApi.copy(item.id, tokenRef.current);
                    setTabs((t) => (t.owned.status === "idle" ? t : { ...t, owned: { ...t.owned, items: [copy, ...t.owned.items] } }));
                    notify(`Created “${copy.name}”`, "info", { label: "Open", run: () => openItem(copy) });
                } catch (err) {
                    notify(`Couldn't make a copy: ${errorText(err)}`, "error");
                }
            })();
        },
        toggleStar: (item) => {
            const next = !item.isFavorite;
            patchEverywhere(item.id, { isFavorite: next });
            setTabs((t) => {
                if (t.starred.status === "idle") return t;
                const without = t.starred.items.filter((i) => i.id !== item.id);
                return { ...t, starred: { ...t.starred, items: next ? [{ ...item, isFavorite: true }, ...without] : without } };
            });
            homeApi.setFavorite(item.id, next, tokenRef.current).catch((err: unknown) => {
                patchEverywhere(item.id, { isFavorite: !next });
                setTabs((t) => ({ ...t, starred: { ...t.starred, status: "idle" } }));
                notify(`Couldn't ${next ? "star" : "unstar"} “${item.name}”: ${errorText(err)}`, "error");
            });
        },
        trash: (item) => setTrashing(item),
        openInDrive: (item) => { window.open(driveUrlFor(item), "_blank", "noopener,noreferrer"); }
    }), [config.openable, editorHref, notify, openItem, patchEverywhere]);

    const submitRename = async (typed: string) => {
        const item = renaming;
        if (!item) return;
        const updated = await homeApi.rename(item.id, withExtension(typed, item.name), tokenRef.current);
        patchEverywhere(item.id, { name: updated.name, updatedAt: updated.updatedAt });
        setRenaming(null);
    };

    const confirmTrash = async () => {
        const item = trashing;
        if (!item) return;
        await homeApi.trash(item.id, tokenRef.current);
        patchEverywhere(item.id, null);
        setTrashing(null);
        notify(`Moved “${item.name}” to the bin`);
    };

    // ── New / open ───────────────────────────────────────────────────────
    const [driveOpen, setDriveOpen] = useState(false);
    const localInputRef = useRef<HTMLInputElement>(null);
    const startNew = (template?: string) => {
        if (template) {
            const state: EditorLaunchState = { template };
            navigate(config.editorPath, { state });
        } else {
            navigate(config.editorPath);
        }
    };

    // Shared-library tiles copy the template into My Drive, then open the
    // copy exactly like a file picked from Drive. The built-in samples
    // (`config.templates`) only show when the library can't be loaded.
    const [catalogue, retryCatalogue] = useCatalogue(config.templateApp, token);
    const builtinFallback = catalogue.status === "error"
        || (catalogue.status === "ready" && catalogue.templates.length === 0);
    const [creatingId, setCreatingId] = useState<string | null>(null);
    const creatingRef = useRef(false);
    const createFrom = (template: SuiteTemplate) => {
        if (creatingRef.current) return;
        creatingRef.current = true;
        setCreatingId(template.id);
        void (async () => {
            try {
                const item = await homeApi.copyTemplate(template.id, tokenRef.current);
                navigate(editorHref(item));
            } catch (err) {
                const what = template.blank ? `a blank ${config.noun}` : `“${template.name}”`;
                notify(
                    `Couldn't create ${what}: ${errorText(err)}`,
                    "error",
                    template.blank ? { label: "Open blank", run: () => startNew() } : undefined
                );
            } finally {
                creatingRef.current = false;
                setCreatingId(null);
            }
        })();
    };
    /** Blank always works: the library's blank file when it's there, else the editor's own empty file. */
    const startBlank = () => {
        if (catalogue.status === "ready" && catalogue.blank) createFrom(catalogue.blank);
        else startNew();
    };
    const blankId = catalogue.status === "ready" ? (catalogue.blank?.id ?? null) : null;

    // Industry picker (remembered per app). "All industries" shows one
    // template per industry — a sampler — so the row stays short; picking an
    // industry lists all of this app's templates for it. An older catalogue
    // with a single industry just lists everything, without the picker.
    const [industryPref, setIndustryPref] = useState<string | null>(() => readPrefText(prefKey("industry")) || null);
    const industries = catalogue.status === "ready" ? catalogue.industries : [];
    const showIndustries = industries.length > 1;
    const industry = showIndustries && industries.some((i) => i.name === industryPref) ? industryPref : null;
    const rowTemplates = useMemo(() => {
        if (catalogue.status !== "ready") return [];
        if (!showIndustries) return catalogue.templates;
        if (industry !== null) return catalogue.templates.filter((t) => t.industry === industry);
        return catalogue.industries.flatMap((i) => {
            const first = catalogue.templates.find((t) => t.industry === i.name);
            return first ? [first] : [];
        });
    }, [catalogue, showIndustries, industry]);
    const rowRef = useRef<HTMLDivElement>(null);
    const changeIndustry = (next: string | null) => {
        setIndustryPref(next);
        writePref(prefKey("industry"), next ?? "");
        rowRef.current?.scrollTo({ left: 0 });
    };

    // The row's scrollbar is hidden (touch swipes it); mouse users get ‹ › buttons.
    const [rowScroll, setRowScroll] = useState({ back: false, forward: false });
    const measureRow = useCallback(() => {
        const row = rowRef.current;
        if (!row) return;
        const back = row.scrollLeft > 1;
        const forward = row.scrollLeft + row.clientWidth < row.scrollWidth - 1;
        setRowScroll((s) => (s.back === back && s.forward === forward ? s : { back, forward }));
    }, []);
    useEffect(() => {
        const row = rowRef.current;
        if (!row) return;
        measureRow();
        const observer = new ResizeObserver(measureRow);
        observer.observe(row);
        return () => observer.disconnect();
    }, [measureRow, rowTemplates, catalogue.status]);
    const scrollRow = (direction: 1 | -1) => {
        const row = rowRef.current;
        if (row) row.scrollBy({ left: direction * Math.max(160, row.clientWidth * 0.8), behavior: "smooth" });
    };
    const tileAccent = { thumb: config.thumb, accentText: config.accent.text, accentSoft: config.accent.soft };
    const onLocalPicked: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const state: EditorLaunchState = { localFile: file };
        navigate(config.editorPath, { state });
    };
    const DriveDialog = config.DriveOpenDialog;

    const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const index = TABS.indexOf(tab);
        let next = index;
        if (e.key === "ArrowRight") next = (index + 1) % TABS.length;
        else if (e.key === "ArrowLeft") next = (index - 1 + TABS.length) % TABS.length;
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = TABS.length - 1;
        else return;
        e.preventDefault();
        const nextTab = TABS[next] ?? "recent";
        setTab(nextTab);
        document.getElementById(`home-tab-${nextTab}`)?.focus();
    };

    const heading = serverSearch || query.trim()
        ? `Results for “${query.trim()}”`
        : tab === "recent" ? `Recent ${plural}` : TAB_LABEL[tab];

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-muted/30">
            <input ref={localInputRef} type="file" accept={config.localAccept} hidden onChange={onLocalPicked} />

            <section aria-labelledby="home-new-heading" className="border-b bg-background">
                <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6">
                    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                        <h1 id="home-new-heading" className="text-base font-semibold sm:text-lg">
                            Start a new {config.noun}
                        </h1>
                        {showIndustries ? (
                            <label className="order-last w-full sm:order-none sm:w-auto">
                                <span className="sr-only">Template industry</span>
                                <select
                                    value={industry ?? ""}
                                    onChange={(e) => changeIndustry(e.target.value || null)}
                                    className={cx(SELECT, "w-full sm:w-auto sm:max-w-[16rem]")}
                                >
                                    <option value="">All industries</option>
                                    {industries.map((i) => (
                                        <option key={i.name} value={i.name}>
                                            {i.name} ({i.count})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : null}
                        <div className="ml-auto flex items-center gap-2">
                            {rowScroll.back || rowScroll.forward ? (
                                <div className="hidden items-center gap-1 sm:flex">
                                    <button
                                        type="button"
                                        onClick={() => scrollRow(-1)}
                                        disabled={!rowScroll.back}
                                        aria-label="Scroll templates back"
                                        title="Scroll back"
                                        className={cx(BTN_ICON, "disabled:opacity-40")}
                                    >
                                        <ChevronLeft className="h-4 w-4" aria-hidden />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => scrollRow(1)}
                                        disabled={!rowScroll.forward}
                                        aria-label="Scroll templates forward"
                                        title="Scroll forward"
                                        className={cx(BTN_ICON, "disabled:opacity-40")}
                                    >
                                        <ChevronRight className="h-4 w-4" aria-hidden />
                                    </button>
                                </div>
                            ) : null}
                            <ActionMenu
                                label="Open file"
                                actions={[
                                    { id: "drive", label: "From Drive", icon: Cloud, onSelect: () => setDriveOpen(true) },
                                    { id: "computer", label: "From this computer", icon: Monitor, onSelect: () => localInputRef.current?.click() }
                                ]}
                                triggerClassName={cx(BTN_OUTLINE, "shrink-0")}
                            >
                                <FolderOpen className="h-4 w-4" aria-hidden />
                                <span>Open file</span>
                                <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
                            </ActionMenu>
                        </div>
                    </div>
                    <div
                        ref={rowRef}
                        onScroll={measureRow}
                        role="group"
                        aria-label={industry ? `${industry} templates` : "Templates"}
                        className="-mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] sm:-mx-6 sm:scroll-px-6 sm:px-6 [&::-webkit-scrollbar]:hidden"
                        aria-busy={catalogue.status === "loading" || undefined}
                    >
                        <TemplateTile
                            label={`Blank ${config.noun}`}
                            {...tileAccent}
                            busy={blankId !== null && creatingId === blankId}
                            disabled={creatingId !== null}
                            onClick={startBlank}
                        />
                        {catalogue.status === "loading" ? (
                            Array.from({ length: 4 }, (_, i) => <TemplateTileSkeleton key={i} />)
                        ) : builtinFallback ? (
                            config.templates.map((t) => (
                                <TemplateTile
                                    key={t.id}
                                    label={t.name}
                                    sublabel={t.description}
                                    title={`${t.name} — ${t.description}`}
                                    art={<BuiltinTemplateArt template={t} thumb={config.thumb} />}
                                    {...tileAccent}
                                    disabled={creatingId !== null}
                                    onClick={() => startNew(t.id)}
                                />
                            ))
                        ) : (
                            rowTemplates.map((t) => (
                                <TemplateTile
                                    key={t.id}
                                    label={t.name}
                                    // The sampler labels each tile with its industry.
                                    sublabel={showIndustries && industry === null ? t.industry : t.description}
                                    title={`${t.name} — ${t.description}`}
                                    art={<SuiteTemplateArt t={t} thumb={config.thumb} />}
                                    {...tileAccent}
                                    busy={creatingId === t.id}
                                    disabled={creatingId !== null}
                                    onClick={() => createFrom(t)}
                                />
                            ))
                        )}
                        {catalogue.status !== "loading" ? <MoreTemplatesTile href={`${SUITE_URLS.drive}/`} /> : null}
                    </div>
                    {catalogue.status === "error" ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                            Showing built-in templates — the suite's template library didn't load ({catalogue.error}).{" "}
                            <button
                                type="button"
                                onClick={retryCatalogue}
                                className="font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                Retry
                            </button>
                        </p>
                    ) : null}
                </div>
            </section>

            <section aria-label="Your files" className="mx-auto w-full max-w-6xl flex-1 px-4 pb-10 pt-4 sm:px-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div
                        role="tablist"
                        aria-label="Which files"
                        onKeyDown={onTabKeyDown}
                        className="-mx-4 flex gap-1 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 lg:shrink-0 [&::-webkit-scrollbar]:hidden"
                    >
                        {TABS.map((t) => {
                            const Icon = TAB_ICON[t];
                            const selected = tab === t;
                            return (
                                <button
                                    key={t}
                                    id={`home-tab-${t}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    aria-controls="home-tabpanel"
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() => setTab(t)}
                                    className={cx(
                                        "inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                        selected ? cx(config.accent.solid, "text-white") : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                    )}
                                >
                                    <Icon className="h-4 w-4" aria-hidden />
                                    {TAB_LABEL[t]}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-2 lg:flex-1 lg:flex-nowrap lg:justify-end">
                        <label className="relative min-w-[10rem] flex-1 sm:w-64 sm:flex-none lg:w-auto lg:max-w-64 lg:flex-1">
                            <span className="sr-only">Search {plural} by name</span>
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape" && query) { e.preventDefault(); setQuery(""); }
                                }}
                                placeholder={`Search ${plural}`}
                                className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
                            />
                            {serverSearch && search.data.status === "loading" ? (
                                <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" aria-hidden />
                            ) : query ? (
                                <button
                                    type="button"
                                    onClick={() => setQuery("")}
                                    aria-label="Clear search"
                                    className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                                >
                                    <X className="h-3.5 w-3.5" aria-hidden />
                                </button>
                            ) : null}
                        </label>
                        {config.allFilesOption ? (
                            <select aria-label="File types" value={scope} onChange={(e) => changeScope(e.target.value as Scope)} className={SELECT}>
                                <option value="app">{config.nounPlural.charAt(0).toUpperCase() + config.nounPlural.slice(1)}</option>
                                <option value="all">All files</option>
                            </select>
                        ) : null}
                        <select aria-label="Sort by" value={sort} onChange={(e) => changeSort(e.target.value as SortKey)} className={SELECT}>
                            {SORTS.map((s) => <option key={s} value={s}>{SORT_LABEL[s]}</option>)}
                        </select>
                        <div role="group" aria-label="Layout" className="flex rounded-md border border-input bg-background p-0.5">
                            {VIEWS.map((v) => {
                                const Icon = v === "grid" ? LayoutGrid : ListIcon;
                                return (
                                    <button
                                        key={v}
                                        type="button"
                                        aria-pressed={view === v}
                                        aria-label={v === "grid" ? "Grid view" : "List view"}
                                        title={v === "grid" ? "Grid view" : "List view"}
                                        onClick={() => changeView(v)}
                                        className={cx(
                                            "flex h-7 w-8 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                            view === v ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground"
                                        )}
                                    >
                                        <Icon className="h-4 w-4" aria-hidden />
                                    </button>
                                );
                            })}
                        </div>
                        <button type="button" onClick={refresh} aria-label="Refresh" title="Refresh" className={BTN_ICON}>
                            <RefreshCw className={cx("h-4 w-4", source.status === "loading" && "animate-spin")} aria-hidden />
                        </button>
                    </div>
                </div>

                <div id="home-tabpanel" role="tabpanel" aria-labelledby={`home-tab-${tab}`} className="mt-5">
                    <div className="mb-3 flex items-baseline justify-between gap-3">
                        <h2 className="truncate text-sm font-semibold text-muted-foreground">{heading}</h2>
                        {!showSkeleton ? (
                            <span className="shrink-0 text-xs text-muted-foreground">
                                {visible.length} {visible.length === 1 ? "item" : "items"}
                            </span>
                        ) : null}
                    </div>

                    {source.status === "error" ? (
                        <div
                            role="alert"
                            className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                        >
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                            <span className="min-w-0 flex-1">Couldn't load your files: {source.error}</span>
                            <button type="button" onClick={retry} className="shrink-0 font-semibold underline-offset-2 hover:underline">
                                Retry
                            </button>
                        </div>
                    ) : null}

                    {showSkeleton ? (
                        <SkeletonCollection view={view} />
                    ) : visible.length > 0 ? (
                        <FileCollection items={visible} view={view} sort={sort} token={token} handlers={handlers} />
                    ) : source.status !== "error" ? (
                        <EmptyState
                            tab={tab}
                            query={query.trim()}
                            noun={scope === "all" ? "file" : config.noun}
                            plural={plural}
                            onNew={startBlank}
                            onShowOwned={() => setTab("owned")}
                        />
                    ) : null}
                </div>
            </section>

            {driveOpen ? (
                <DriveDialog
                    onClose={() => setDriveOpen(false)}
                    onPick={(item) => {
                        setDriveOpen(false);
                        navigate(editorHref(item));
                    }}
                />
            ) : null}
            {renaming ? (
                <RenameDialog initialName={renaming.name} onCancel={() => setRenaming(null)} onSubmit={submitRename} />
            ) : null}
            {trashing ? (
                <ConfirmDialog
                    title="Move to bin?"
                    message={<>“{trashing.name}” will be moved to the bin in Drive. You can restore it from there for 30 days.</>}
                    confirmLabel="Move to bin"
                    onCancel={() => setTrashing(null)}
                    onConfirm={confirmTrash}
                />
            ) : null}
            <Toast toast={toast} onDismiss={dismissToast} />
        </div>
    );
}

function EmptyState({
    tab, query, noun, plural, onNew, onShowOwned
}: {
    tab: Tab;
    query: string;
    noun: string;
    plural: string;
    onNew: () => void;
    onShowOwned: () => void;
}) {
    const Icon = query ? Search : TAB_ICON[tab];
    let title: string;
    let body: string;
    if (query) {
        title = `No ${plural} match “${query}”`;
        body = tab === "shared"
            ? "This tab only searches files other people shared with you."
            : "Try another name. Files shared with you are under Shared with me.";
    } else if (tab === "recent") {
        title = `No recent ${plural}`;
        body = `${plural.charAt(0).toUpperCase() + plural.slice(1)} you open show up here.`;
    } else if (tab === "starred") {
        title = "Nothing starred yet";
        body = `Star ${plural} to find them here quickly.`;
    } else if (tab === "shared") {
        title = "Nothing shared with you yet";
        body = `When someone shares a ${noun} with you, it shows up here.`;
    } else {
        title = `You don't have any ${plural} yet`;
        body = `Start a new ${noun}, or open one from your computer.`;
    }
    return (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-card px-6 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Icon className="h-6 w-6 text-muted-foreground" aria-hidden />
            </span>
            <p className="text-sm font-medium">{title}</p>
            <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
            {!query && (tab === "recent" || tab === "owned") ? (
                <div className="flex flex-wrap justify-center gap-2">
                    <button type="button" onClick={onNew} className={BTN_PRIMARY}>
                        <Plus className="h-4 w-4" aria-hidden />
                        Blank {noun}
                    </button>
                    {tab === "recent" ? (
                        <button type="button" onClick={onShowOwned} className={BTN_OUTLINE}>
                            See all your {plural}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
